import { useEffect, useState } from 'react';
import type { IconName } from './ui';
import { Icon } from './ui';
import { listActivity } from './api';
import { useSasEnAttente } from './sasEnAttente';
import { Link, paths, useRoute } from './router';
import type { Route } from './router';

/**
 * Navigation principale (US-56).
 *
 * ### Ce qu'elle remplace
 *
 * L'application comptait dix routes et **aucune navigation persistante** :
 * la barre de l'en-tête était conditionnée à l'accueil, si bien que depuis les
 * réglages, les courses, le journal, la semaine ou une fiche, il ne restait
 * que le logo et un « ← Bibliothèque ». Et cette barre, sur l'accueil, était
 * un `<nav aria-label="Créer une recette">` contenant **huit items dont quatre
 * ne créaient rien** — un lecteur d'écran annonçait « navigation, Créer une
 * recette » puis « Journal », « Réglages ».
 *
 * ### Cinq destinations, et pas une de plus
 *
 * Cinq est la limite de ce qui reste lisible en bas d'un téléphone : au-delà,
 * les libellés se tronquent et l'on navigue à l'icône, donc au hasard.
 *
 * **« Moi » regroupe le compte, les préférences et la déconnexion** — qui
 * n'avait jusqu'ici aucune sortie accessible depuis l'en-tête, seulement un
 * bouton `ghost` en milieu de la troisième section des réglages.
 *
 * **Ajouter une recette n'est pas ici**, et c'est délibéré : c'est une
 * **action**, pas une destination. Elle a son bouton, sur la bibliothèque.
 *
 * ### En bas sur mobile
 *
 * Là où le pouce arrive. En haut sur grand écran, où la souris parcourt de
 * toute façon toute la fenêtre et où une barre basse mangerait de la hauteur
 * utile.
 */

interface Entree {
  readonly to: string;
  readonly label: string;
  readonly icon: IconName;
  /** Routes qui allument cette entrée — une fiche appartient à la bibliothèque. */
  readonly routes: readonly Route['name'][];
}

/**
 * Nombre de nouveautés depuis la dernière visite (US-68). Lu une fois au
 * montage : ce n'est pas une notification poussée — une famille de trois
 * personnes n'a pas besoin d'être interrompue.
 */
function useNouveautes(modePartage: boolean): number {
  const [nouveautes, setNouveautes] = useState(0);
  const route = useRoute();

  useEffect(() => {
    if (modePartage) return;
    const controller = new AbortController();
    Promise.resolve()
      .then(() => listActivity(controller.signal))
      .then((flux) => { setNouveautes(typeof flux.unseen === 'number' ? flux.unseen : 0); })
      .catch(() => { /* un repère absent vaut mieux qu'une navigation cassée */ });
    return () => { controller.abort(); };
    // Relu en arrivant sur le journal : c'est là que le repère est remis à zéro.
  }, [modePartage, route.name]);

  // **Zéro tant qu'on est sur la tablette, et pas seulement « pas relu »**
  // (US-80). `modePartage` n'est connu qu'une fois la session lue : au premier
  // rendu il vaut `false`, la lecture part, et rendre `nouveautes` tel quel
  // laisserait la pastille afficher un compte obtenu avant qu'on sache où l'on
  // est. Sortir de l'effet par `if (modePartage) return;` ne suffit pas :
  // sortir n'est pas remettre à zéro.
  //
  // C'est la **ceinture**, pas la correction : le repère faux est corrigé côté
  // serveur (`GET /api/activite` répond `unseen: 0` à une session d'appareil).
  // Filtrer ici seul laisserait la route répondre « 50 nouveautés » au
  // prochain appelant.
  //
  // Même dernière ligne, mot pour mot, que `useSasEnAttente`
  // (sasEnAttente.ts) : un troisième compteur devra la recopier sciemment.
  return modePartage ? 0 : nouveautes;
}

/**
 * La pastille, un seul point de vérité pour les deux qui existent (US-68,
 * US-71). Le nombre est lu à voix haute avec le libellé : une pastille muette
 * ne dit rien à qui n'y voit pas. Jamais rendue à zéro — « aucune puce ne porte
 * le compteur zéro » (§4 CLAUDE.md).
 */
function Pastille({ nombre, testId, label }: { nombre: number; testId: string; label: string }): React.JSX.Element | null {
  if (nombre <= 0) return null;
  return (
    <span
      data-testid={testId}
      aria-label={label}
      className="rounded-full bg-primary px-1.5 text-[0.65rem] font-semibold text-primary-foreground"
    >
      {nombre}
    </span>
  );
}

export const ENTREES: readonly Entree[] = [
  {
    to: paths.library,
    label: 'Recettes',
    icon: 'livre',
    // **Les collections restent sous « Recettes »** (US-63). Le plan prévoyait
    // une sixième entrée ; la décision d'US-56 en fixait cinq, et la densité de
    // la barre est mesurée par un test. Une collection est une façon de lire sa
    // bibliothèque, pas une destination de plus : elle s'atteint depuis
    // l'accueil, et l'onglet reste allumé quand on y est.
    // **Le sas est sous « Recettes »** (US-71), et sa pastille aussi. Trois
    // raisons, dans cet ordre : « Journal » porte déjà celle d'US-68, et deux
    // compteurs sur une même entrée cessent tous les deux de vouloir dire
    // quelque chose ; « Recettes » regroupe déjà les trois écrans d'import et
    // les collections, dont le sas est le quatrième chemin d'entrée ; et la
    // seule issue du sas est la bibliothèque — « Garder » y fait entrer la
    // recette. La pastille annonce ce qui attend **à la porte**, elle ne
    // compte pas des recettes du livre : les compteurs de la bibliothèque,
    // eux, restent rigoureusement inchangés.
    routes: ['library', 'archives', 'recipe', 'edit-recipe', 'cook', 'new-recipe',
      'import-url', 'import-paste', 'import-photo', 'import-inbox', 'collections'],
  },
  // La semaine précède les courses parce qu'elle les précède dans la vie
  // (US-36) : on décide ce qu'on mange, puis on achète ce que cela demande.
  { to: paths.mealPlan, label: 'Semaine', icon: 'calendrier', routes: ['meal-plan'] },
  { to: paths.shopping, label: 'Courses', icon: 'panier', routes: ['shopping'] },
  // Le journal porte la différenciation du produit (US-30) : ce que *vous*
  // avez cuisiné, et ce que vous en avez pensé.
  { to: paths.journal, label: 'Journal', icon: 'carnet', routes: ['journal'] },
  { to: paths.settings, label: 'Moi', icon: 'personne', routes: ['settings'] },
];

export interface NavigationProps {
  /**
   * Mode cuisine partagée (US-50). La tablette du plan de travail ne gère ni
   * courses ni compte : les entrées qui répondraient 404 ne s'affichent pas,
   * plutôt que de laisser découvrir l'absence au moment de l'échec.
   */
  modePartage?: boolean;
}

export function Navigation({ modePartage = false }: NavigationProps): React.JSX.Element {
  const route = useRoute();
  /**
   * Le repère de nouveautés (US-68), posé sur « Journal ».
   *
   * Il vit ici plutôt que dans une sixième entrée : la décision d'US-56 fixe
   * cinq destinations, et leur nombre est mesuré par un test. « Quoi de neuf »
   * n'est pas un endroit de plus, c'est ce que le journal familial a de neuf.
   *
   * Muet en mode cuisine partagée : une tablette de plan de travail n'a pas de
   * compte, donc pas de repère de lecture.
   */
  const nouveautes = useNouveautes(modePartage);
  /**
   * Le sas (US-71), posé sur « Recettes » — voir la note de l'entrée
   * ci-dessus. Muet en mode cuisine partagée pour la même raison que le
   * repère de nouveautés : une tablette de plan de travail n'a pas de compte,
   * donc pas de sas.
   *
   * Le hook vit dans `sasEnAttente.ts` depuis US-72 : la bibliothèque porte
   * désormais **le lien** vers le sas, et elle a besoin du même compte. La
   * pastille annonçait jusque-là un endroit qu'aucun lien n'atteignait.
   */
  const sas = useSasEnAttente(modePartage);
  const entrees = modePartage
    ? ENTREES.filter((entree) => entree.to === paths.library || entree.to === paths.journal)
    : ENTREES;

  return (
    <nav
      aria-label="Navigation principale"
      className={[
        // Mobile : fixée en bas, au-dessus du contenu, sous les minuteurs
        // (`z-[60]`) qui doivent rester visibles par-dessus tout.
        'fixed inset-x-0 bottom-0 z-50 border-t border-border bg-surface',
        // Grand écran : elle rejoint le flux, sous la signature. Même élément,
        // déplacé par le CSS — la monter deux fois produirait deux `<nav>` et
        // des liens en double dans l'arbre d'accessibilité.
        'lg:static lg:z-auto lg:border-t-0 lg:border-b lg:bg-transparent lg:pb-2',
        'flex justify-around lg:justify-start lg:gap-1',
        // Marge de sécurité pour les téléphones à encoche.
        'pb-[env(safe-area-inset-bottom)] lg:pb-0',
      ].join(' ')}
    >
      {entrees.map((entree) => {
        const actif = entree.routes.includes(route.name);
        return (
          <Link
            key={entree.to}
            to={entree.to}
            // `aria-current="page"` et non une simple classe : l'état actif
            // doit s'entendre autant qu'il se voit.
            aria-current={actif ? 'page' : undefined}
            className={[
              'flex min-h-touch flex-1 flex-col items-center justify-center gap-0.5 px-2 py-1.5',
              'text-xs font-medium no-underline transition-colors',
              'lg:flex-none lg:flex-row lg:gap-2 lg:rounded-control lg:px-3 lg:text-sm',
              actif
                ? 'text-primary lg:bg-surface-raised'
                : 'text-foreground-secondary hover:text-foreground',
            ].join(' ')}
          >
            {/* Une seule icône (US-89) : deux `<Icon>` co-existaient, l'un
                censé se cacher via `lg:hidden`/`hidden lg:inline-block` — mais
                `Icon` code `inline-block` en dur dans ses propres classes, et
                dans le CSS compilé cette classe non conditionnelle arrive
                après `.hidden` : à spécificité égale, le `hidden` de
                l'appelant ne pouvait jamais gagner sur mobile, où les deux
                icônes s'affichaient. Une icône, dimensionnée par
                `sizeClassName` (largeur/hauteur responsives) plutôt que par
                deux éléments dont un se cache. */}
            <Icon
              name={entree.icon}
              sizeClassName="w-[1.35em] h-[1.35em] lg:w-[1em] lg:h-[1em]"
            />
            {entree.label}
            {entree.to === paths.journal && (
              <Pastille
                nombre={nouveautes}
                testId="repere-nouveautes"
                label={`${String(nouveautes)} nouveauté${nouveautes > 1 ? 's' : ''}`}
              />
            )}
            {entree.to === paths.library && (
              <Pastille
                nombre={sas}
                testId="repere-sas"
                label={`${String(sas)} recette${sas > 1 ? 's' : ''} en attente dans le sas`}
              />
            )}
          </Link>
        );
      })}
    </nav>
  );
}

/**
 * Hauteur réservée sous le contenu pour que la barre basse ne recouvre jamais
 * la fin d'un écran. En classe utilitaire plutôt qu'en mesure : la barre a une
 * hauteur fixe, et la mesurer à l'exécution ferait payer un reflow à chaque
 * rendu pour une valeur qui ne change pas.
 */
export const ESPACE_NAVIGATION = 'pb-20 lg:pb-0';
